<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { usePermissions } from '@/composables/usePermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import _ from '@/setup/lodash';
import updateUrl from '@/service/updateUrl';
import { useApi } from '@/api';
import type { UiTableNewSortEmit } from '@/types/table';
import type {
  GetMediaBuyingPaymentMethodsBlacklistData,
  GetMediaBuyingPaymentMethodsBlacklistResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingPaymentMethodsBlacklistRow = GetMediaBuyingPaymentMethodsBlacklistResponse['payload'][number];
//#endregion

//#region const
const LS_PATH = 'MediaBuying/BlackListWallets';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields = [
  {
    name: 'media_buying_payment_method_name',
    width: '215',
    align: 'left',
    class: 'text',
  },
  {
    name: 'payment_wallet',
    align: 'left',
    class: 'text',
  },
  {
    name: 'created_at',
    width: '120',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const popupManager = usePopupManager();
const { t } = useI18n();
const { hasPermission } = usePermissions();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<MediaBuyingPaymentMethodsBlacklistRow[]>([]);
//#endregion

//#region computed
const isBlacklistEdit = computed(() => {
  return hasPermission('media_buying_payment_methods_blacklist_edit');
});
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetMediaBuyingPaymentMethodsBlacklistData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset']),
    page: page.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingBlacklist(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;
    _.merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
  }

  nextTick(() => {
    fetchData();
  });
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function handleOpenBlacklistWalletPopup(row?: MediaBuyingPaymentMethodsBlacklistRow) {
  const data = row
    ? {
      id: row.id,
      data: {
        payment_wallet: row.payment_wallet,
        media_buying_payment_method_id: row.media_buying_payment_method_id,
      },
    }
    : undefined;

  popupManager.blacklistWalletPopup.open({
    data,
    listeners:{
      success() {
        fetchData();
      },
    },
  });
}

function removeMethod(row: MediaBuyingPaymentMethodsBlacklistRow) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.blacklistWallets.popup.delete.msg', { address: row.payment_wallet }),
      title: t('mediaBuying.blacklistWallets.popup.delete.title'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.blacklistWallets.popup.delete.action'),
      confirmType: 'negative',
      confirmIcon: 'trash-alt',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await api.deleteMediaBuyingBlacklist(row.id);
        await fetchData(true);
        context.handleClose();
      },
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-black-list-wallets">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('mediaBuying.blacklistWallets.title') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="isBlacklistEdit"
          filled
          icon="plus"
          @click="handleOpenBlacklistWalletPopup"
        >
          {{ $t('mediaBuying.blacklistWallets.add') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.blacklistWallets"
      @sort="handleSort"
    >
      <template
        v-if="isBlacklistEdit"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('crm.buttons.edit')"
              @click="handleOpenBlacklistWalletPopup(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              @click="removeMethod(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
